/**
 * Shared OData URL builders + payload helpers for list pages.
 *
 * Previously lived inside listPageStyles.js. This module is the new home;
 * listPageStyles.js re-exports from here for backwards compatibility.
 */

export function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function buildODataUrl(base, { pageSize, currentPage, sortField, sortDir, filterParams, expand }) {
  const skip = (currentPage - 1) * pageSize;
  let url = `${base}?$count=true&$top=${pageSize}&$skip=${skip}`;
  if (filterParams) url += `&$filter=${encodeURIComponent(filterParams)}`;
  if (expand) url += `&$expand=${encodeURIComponent(expand)}`;
  url += `&$orderby=${encodeURIComponent(`${sortField} ${sortDir}`)}`;
  return url;
}

export function buildODataCountUrl(base, { filterParams, expand } = {}) {
  let url = `${base}?$count=true&$top=0`;
  if (filterParams) url += `&$filter=${encodeURIComponent(filterParams)}`;
  if (expand) url += `&$expand=${encodeURIComponent(expand)}`;
  return url;
}

export function buildODataScanUrl(base, { pageSize, skip = 0, filterParams, expand } = {}) {
  let url = `${base}?$top=${pageSize}&$skip=${skip}`;
  if (filterParams) url += `&$filter=${encodeURIComponent(filterParams)}`;
  if (expand) url += `&$expand=${encodeURIComponent(expand)}`;
  return url;
}

export function getODataRows(data) {
  return data?.value ?? data?.data ?? data?.Data ?? (Array.isArray(data) ? data : []);
}

export function getExplicitODataTotalCount(data) {
  const exactCount = data?.['@odata.count'] ?? data?.['@OData.Count'] ?? data?.count ?? data?.Count;
  const parsedExactCount = typeof exactCount === 'string' ? Number(exactCount) : exactCount;
  return Number.isFinite(parsedExactCount) ? parsedExactCount : null;
}

function tryParseNumericText(text) {
  const normalized = typeof text === 'string' ? text.trim() : '';
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

async function readCountResponse(response) {
  const raw = await response.text();
  const directCount = tryParseNumericText(raw);
  if (directCount != null) return directCount;
  try {
    return getExplicitODataTotalCount(JSON.parse(raw));
  } catch {
    return null;
  }
}

async function scanODataTotalCount({ base, filterParams, expand, headers }) {
  const batchSize = 1000;
  let total = 0;
  let skip = 0;
  for (let attempt = 0; attempt < 1000; attempt += 1) {
    const response = await fetch(buildODataScanUrl(base, { pageSize: batchSize, skip, filterParams, expand }), { headers });
    if (!response.ok) return null;
    const payload = await response.json();
    const rows = getODataRows(payload);
    total += rows.length;
    const explicit = getExplicitODataTotalCount(payload);
    if (explicit != null) return explicit;
    if (rows.length < batchSize) return total;
    skip += batchSize;
  }
  return total;
}

export function getODataTotalCount(data, rows, currentPage, pageSize) {
  const explicitCount = getExplicitODataTotalCount(data);
  if (explicitCount != null) return explicitCount;
  const rowCount = rows?.length ?? 0;
  const loadedUntil = Math.max(0, currentPage - 1) * pageSize + rowCount;
  return loadedUntil + (rowCount === pageSize ? 1 : 0);
}

export async function resolveODataTotalCount({
  data, rows, currentPage, pageSize, base, filterParams, expand, headers,
}) {
  const exactCount = getODataTotalCount(data, rows, currentPage, pageSize);
  const rowCount = rows?.length ?? 0;
  const heuristic = Math.max(0, currentPage - 1) * pageSize + rowCount + (rowCount === pageSize ? 1 : 0);
  if (exactCount !== heuristic || !base) return exactCount;
  try {
    const response = await fetch(buildODataCountUrl(base, { filterParams, expand }), { headers });
    if (!response.ok) {
      const scanned = await scanODataTotalCount({ base, filterParams, expand, headers });
      return scanned ?? exactCount;
    }
    const resolved = await readCountResponse(response);
    if (resolved != null) return resolved;
    const scanned = await scanODataTotalCount({ base, filterParams, expand, headers });
    return scanned ?? exactCount;
  } catch {
    return exactCount;
  }
}
